




















































































import { Vue, Component, Lifecycle } from "av-ts";
import Papa from "papaparse";

import DatePicker from "vue-flatpickr-component";
import Fetcher from "../../services/Fetcher";
import UserService from "../../services/UserService";
import TopicService from "../../services/TopicService";
import { addEventsToQueue, isAdmin } from "../../util";
import { IUserSummary, IOfferingUser, ITopic } from "../../interfaces/models";

@Component({
    components: {
        DatePicker
    }
})
export default class ReportsView extends Vue {
    
    reportsList: String[][] = [
        ["User Report", "Aggregate user data across all areas of the platform"],
        ["Consented User Report", "Aggregate user data from consented users only"],
        ["User Topic Report", "User ratings per course topic"],
        ["User Topic History Report", "User rating changes per topic over time"],
        ["Question Report", "All question data including user ratings & answers"],
        ["Answer Report", "Answer log including associated user ratings and question difficulty"],
        ["Comment Report", "All comments made on current questions"],
        ["Consent Report", "Consent status for each user"]];

    startDate: Date | undefined = undefined;
    endDate: Date | undefined = undefined;
    offeringUser: IOfferingUser | undefined = undefined;
    courseCode: string = "";
    topics: ITopic[] | undefined = undefined;

    get startingDate() {
        if (this.startDate === undefined) {
            return "";
        } else {
            return this.startDate.getFullYear() + "-" + ("0" + (this.startDate.getMonth() + 1)).slice(-2) + "-"
                + ("0" + this.startDate.getDate()).slice(-2);
        }
    }

    get endingDate() {
        if (this.endDate === undefined) {
            return "";
        } else {
            return this.endDate.getFullYear() + "-" + ("0" + (this.endDate.getMonth() + 1)).slice(-2) + "-"
                + ("0" + this.endDate.getDate()).slice(-2);
        }
    }

    set startingDate(newDateStr: string) {
        if (newDateStr === undefined || newDateStr == "") {
            this.startDate = undefined;
        } else {
            let newDate = new Date(newDateStr);
            this.startDate = newDate;
        }
    }

    set endingDate(newDateStr: string) {
        if (newDateStr === undefined || newDateStr == "") {
            this.endDate = undefined;
        } else {
            let newDate = new Date(newDateStr);
            this.endDate = newDate;
        }
    }

    get endDateInclusive() {
        if (this.endDate) {
            let date = new Date(this.endDate);
            date.setDate(this.endDate.getDate() + 1);
            return date;
        }
    }

    updateOfferingUser(offeringUser: IOfferingUser) {
        if (!isAdmin(offeringUser.roles)) {
            this.$router.push("error/403");
        }
        this.offeringUser = offeringUser;
        this.courseCode = this.offeringUser.offering.courseCode;
    }

    updateTopics(topics: ITopic[]) {
        this.topics = topics;
    }

    downloadReport(reportName: string) {
        const startDate = this.startDate;
        const endDate = this.endDateInclusive;
        const fields: string[] = this.getFields(reportName);
        const toCSV = (results: IUserSummary[]) => this.downloadCSV(this.makeCSVString(results, fields));
        this.addNotification(reportName);
        if (reportName == "User Report") {
            UserService.getUserStats(startDate, endDate).then(toCSV);
        } else if (reportName == "Consented User Report") {
            UserService.getConsentedUserStats(startDate, endDate).then(toCSV);
        } else if (reportName == "User Topic Report") {
            UserService.getUserTopicReport(startDate, endDate).then(x => this.downloadCSVFromServer(x));
        } else if (reportName == "User Topic History Report") {
            UserService.getUserTopicHistoryReport(startDate, endDate).then(x => this.downloadCSVFromServer(x));
        } else if (reportName == "Question Report") {
            UserService.getQuestionReport(startDate, endDate).then(x => this.downloadCSVFromServer(x));
        } else if (reportName == "Answer Report") {
            UserService.getAnswerReport(startDate, endDate).then(x => this.downloadCSVFromServer(x));
        } else if (reportName == "Comment Report") {
            UserService.getCommentReport(startDate, endDate).then(x => this.downloadCSVFromServer(x));
        } else if (reportName == "Consent Report") {
            UserService.getConsentReport(startDate, endDate).then(x => this.downloadCSVFromServer(x));
        }
    }

    addNotification(reportName: string) {
        addEventsToQueue([{
            name: "Report Downloading",
            description: reportName + " will download shortly.",
            icon: "arrow_downward"
        }]);
    }

    getFields(reportName: string): string[] {
        let fields: string[] = [];
        if (reportName == "User Report") {
            fields = ["id", "studentNumber", "firstName", "lastName", "userRating", "questionsAuthored",
                "questionEffectiveness", "questionsAnswered", "questionsAnsweredCorrectly",
                "questionsRated", "achievementsEarned"];
        } else if (reportName == "Consented User Report") {
            fields = ["rank", "questionsAuthored", "questionsAnswered",
                "questionsAnsweredCorrectly", "achievementsEarned", "questionsRated"];
        } else if (reportName == "User Topic Report") {
            fields = ["userID", "studentNumber", "lastName",
                "firstName", "overallELO"];
            if (this.topics) {
                for (let topic of this.topics) {
                    fields.push(topic.name);
                }
            }
        } else if (reportName == "User Topic History Report") {
            fields = ["id", "studentNumber", "firstName", "lastName", "topicID", "topicName",
                "topicRating", "timestamp"];
        } else if (reportName == "Question Report") {
            fields = ["questionID", "timestamp", "author", "avgCorrectness", "avgEffectiveness", "answerCount",
                "question", "optionA", "optionB", "optionC", "optionD", "explanation", "topics"];
        } else if (reportName == "Answer Report") {
            fields = ["userID", "questionID", "timestamp", "userELO", "questionELO", "questionEffectiveness",
                "questionCorrectness", "effectivenessRating", "correctnessRating"];
        } else if (reportName == "Comment Report") {
            fields = ["id", "studentNumber", "userID", "questionID", "parentComment", "text"];
        } else if (reportName == "Consent Report") {
            fields = ["studentNumber", "userID", "consented", "timestamp"];
        }
        return fields;
    }
    makeCSVString(userData: IUserSummary[], fields: string[]) {
        if (userData.length == 0) {
            return;
        }

        const unparseData = {
            fields: fields,
            data: userData
        };

        return Papa.unparse(unparseData, {
            quotes: true,
            delimiter: ",",
            newline: "\r\n"
        });
    }

    downloadCSV(csvString: undefined | string) {
        // update CSV string headings
        if (!csvString) {
            return addEventsToQueue([{
                name: "No Consent Data",
                description: "There are no consented users for your course",
                icon: "error"
            }]);
        }

        csvString = this.updateHeadings(csvString);
        // Encode the CSV string as a URI
        const uri = encodeURI("data:text/csv;charset=utf-8," + csvString);
        // Create a mock anchor element and point it at the CSV URI
        const _a = document.createElement("a");
        _a.target = "_blank";
        _a.href = uri;
        _a.download = this.courseCode + "_ripple_export_" + Date.now() + ".csv";
        _a.style.opacity = "0";

        // Add the anchor tag to the DOM and programmatically click it
        document.body.appendChild(_a);
        _a.dispatchEvent(new MouseEvent("click", {
            bubbles: false
        }));

        // Remove the anchor element from the DOM after it has been clicked
        document.body.removeChild(_a);
    }

    updateHeadings(csvString:string) {
        let headings = [["id", "ID"],
            ["firstName", "First Name"],
            ["lastName", "Last Name"],
            ["rank", "Rank"],
            ["questionsAuthored", "Questions Authored"],
            ["questionsAnswered", "Questions Answered"],
            ["questionsAnsweredCorrectly", "Questions Answered Correctly"],
            ["achievementsEarned", "Achievements Earned"],
            ["questionsRated", "Questions Rated"],
            ["userRating", "User Rating"],
            ["questionEffectiveness", "Question Effectiveness"],
            ["studentNumber", "Student Number"],
            ["topicName", "Topic Name"],
            ["topicID", "Topic ID"],
            ["topicRating", "Topic Rating"],
            ["timestamp", "Timestamp"],
            ["questionID", "Question ID"],
            ["author", "Author"],
            ["avgCorrectness", "Average Correctness"],
            ["avgEffectiveness", "Average Effectiveness"],
            ["answerCount", "Answer Count"],
            ["question", "Question"],
            ["optionA", "Option A"],
            ["optionB", "Option B"],
            ["optionC", "Option C"],
            ["optionD", "Option D"],
            ["explanation", "Explanation"],
            ["topics", "Topics"],
            ["userID", "User ID"],
            ["userELO", "User ELO"],
            ["questionELO", "Question ELO"],
            ["questionCorrectness", "Question Correctness"],
            ["effectivenessRating", "Effectiveness Rating"],
            ["correctnessRating", "Correctness Rating"],
            ["parentComment", "Parent Comment"],
            ["text", "Text"],
            ["consented", "Consented"]];

        let splitString = csvString.split(/\r\n|\r|\n/);

        let headerString = splitString[0];
        for (let i of headings) {
            headerString = headerString.replace(i[0], i[1]);
        }
        splitString[0] = headerString;
        csvString = splitString.join("\r\n");
        return csvString;
    }

    downloadCSVFromServer(path: string) {
        const _a = document.createElement("a");
        _a.href = path;
        _a.download = this.courseCode + "_ripple_export_" + Date.now() + ".csv";
        _a.style.opacity = "0";

        // Add the anchor tag to the DOM and programmatically click it
        document.body.appendChild(_a);
        _a.dispatchEvent(new MouseEvent("click", {
            bubbles: false
        }));
        document.body.removeChild(_a);
    }

    @Lifecycle
    created() {
        Fetcher.get(UserService.getLoggedInUser)
            .on(this.updateOfferingUser);
        Fetcher.get(TopicService.getAllAvailableTopics)
            .on(this.updateTopics);
    }

    @Lifecycle
    destroyed() {
        Fetcher.get(UserService.getLoggedInUser)
            .off(this.updateOfferingUser);
        Fetcher.get(TopicService.getAllAvailableTopics)
            .off(this.updateTopics);
    }
}



// WEBPACK FOOTER //
// ./node_modules/vue-loader/lib/selector.js?type=script&index=0!./src/components/admin/ReportsView.vue